// @spec BCP-STR-027
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useExerciseDetail } from '@/features/bcp/api/exercises';
import { ExerciseConductForm } from '@/features/bcp/components/ExerciseConductForm';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/bcp/exercises/$exerciseId/conduct')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.conductExercise'),
  }),
  component: ExerciseConductPage,
});

function ExerciseConductPage() {
  const { t } = useTranslation('bcp');
  const { exerciseId } = Route.useParams();
  const navigate = useNavigate();
  const { data: exercise, isLoading, isError } = useExerciseDetail(exerciseId);

  const handleBack = () => {
    void navigate({ to: '/bcp/exercises/$exerciseId', params: { exerciseId } });
  };

  const handleComplete = () => {
    void navigate({ to: '/bcp/exercises/$exerciseId', params: { exerciseId } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !exercise) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-destructive">{t('exercises.failedToLoadExercise')}</p>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            {t('exercises.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Breadcrumb / Back nav */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('exercises.backToExercise')}
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">
            {t('exercises.breadcrumbConduct', { name: exercise.name })}
          </span>
        </div>

        <h1 className="text-2xl font-bold">{t('exercises.conductPageTitle')}</h1>

        <ExerciseConductForm exercise={exercise} onComplete={handleComplete} />
      </div>
    </div>
  );
}
